import {
  createContext, ReactElement, ReactNode, useCallback, useContext, useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '~/contexts/newToastContext';
import { getOverviewUnitHealth } from '../helpers/overview';
import { FilterContext } from './FilterContext';
import { parseHealthData, parseUnits } from '../helpers/health';
import { HealthVariant } from '../Health/components/UnitsInfo/types';

export interface HealthCountInfo {
  healthy: number,
  alert: number,
  critical: number,
  inoperative: number,
}

export type UnitsInfo = {
  id: number;
  name: string;
  lat: number | null;
  lon: number | null;
  state: string;
  city: string;
  health: HealthVariant;
  machines: HealthCountInfo;
}[]

export interface HealthDataContext {
  data: HealthCountInfo | undefined;
  units: UnitsInfo;
  loading: boolean;
  refetch: (client: number, selectedOptions: FilterContext['selectedOptions']) => Promise<void>;
}

const HealthDataContext = createContext<HealthDataContext | undefined>(undefined);

export const HealthDataProvider = ({
  children,
}: Readonly<{ children: ReactNode }>): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<HealthCountInfo | undefined>();
  const [units, setUnits] = useState<UnitsInfo>([]);
  const { toast } = useToast();
  const { t } = useTranslation();

  const getHealthInfo = useCallback(async (
    client: number,
    selectedOptions: FilterContext['selectedOptions'],
  ): Promise<void> => {
    setLoading(true);

    const data = await getOverviewUnitHealth(client, {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
    });

    if (data) {
      setData(parseHealthData(data));
      setUnits(parseUnits(data.UNITS));
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosSaudeUnidades'), 'error');
    }

    setLoading(false);
  }, []);

  const value = useMemo((): HealthDataContext => ({
    data,
    loading,
    units,
    refetch: getHealthInfo,
  }), [data, getHealthInfo, loading, units]);

  return (
    <HealthDataContext.Provider value={value}>
      {children}
    </HealthDataContext.Provider>
  );
};

export const useHealthDataContext = (): HealthDataContext => {
  const context = useContext(HealthDataContext);

  if (!context) {
    throw new Error('useHealthDataContext must be used within a HealthDataProvider.');
  }

  return context;
};
